import { supabase } from '../../supabase.js';
import { validateAndGetTenantUuid } from '../../utils/tenantUtils.js';
import { ServiceError } from '../../utils/errorHandler.js';

class AppAdminReviewService {
  constructor() {
    this.reviewsTable = 'app_reviews';
    this.appsTable = 'apps';
    this.developersTable = 'app_developers';
  }

  // Review Management
  async createReview(reviewData, tenant_id) {
    try {
      const tenantUuid = await validateAndGetTenantUuid(tenant_id);
      
      const { data, error } = await supabase
        .from(this.reviewsTable)
        .insert({
          ...reviewData,
          review_date: new Date().toISOString()
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      throw new ServiceError('REVIEW_CREATION_FAILED', error.message);
    }
  }

  async getReviewById(reviewId, tenant_id) {
    try {
      const tenantUuid = await validateAndGetTenantUuid(tenant_id);
      
      const { data, error } = await supabase
        .from(this.reviewsTable)
        .select(`
          *,
          app:apps (
            id,
            app_name,
            app_code,
            app_status,
            developer_id
          ),
          reviewer:users (
            id,
            email,
            full_name
          )
        `)
        .eq('id', reviewId)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      throw new ServiceError('REVIEW_NOT_FOUND', error.message);
    }
  }

  async updateReview(reviewId, updateData, tenant_id) {
    try {
      const tenantUuid = await validateAndGetTenantUuid(tenant_id);
      
      const { data, error } = await supabase
        .from(this.reviewsTable)
        .update({
          ...updateData,
          review_date: new Date().toISOString()
        })
        .eq('id', reviewId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      throw new ServiceError('REVIEW_UPDATE_FAILED', error.message);
    }
  }

  // Get Pending Reviews
  async getPendingReviews(tenant_id, filters = {}) {
    try {
      const tenantUuid = await validateAndGetTenantUuid(tenant_id);
      
      let query = supabase
        .from(this.reviewsTable)
        .select(`
          *,
          app:apps (
            id,
            app_name,
            app_code,
            app_category,
            app_subcategory,
            app_version,
            app_status,
            developer_id,
            created_at
          ),
          reviewer:users (
            id,
            email,
            full_name
          )
        `)
        .eq('review_status', 'pending');

      // Apply filters
      if (filters.category) {
        query = query.eq('app.app_category', filters.category);
      }
      if (filters.reviewerId) {
        query = query.eq('reviewer_id', filters.reviewerId);
      }

      const { data, error } = await query.order('review_date', { ascending: false });

      if (error) throw error;
      return data;
    } catch (error) {
      throw new ServiceError('PENDING_REVIEWS_FETCH_FAILED', error.message);
    }
  }

  // Get Reviews by App
  async getAppReviews(appId, tenant_id, filters = {}) {
    try {
      const tenantUuid = await validateAndGetTenantUuid(tenant_id);
      
      let query = supabase
        .from(this.reviewsTable)
        .select(`
          *,
          reviewer:users (
            id,
            email,
            full_name
          )
        `)
        .eq('app_id', appId);

      // Apply filters
      if (filters.status) {
        query = query.eq('review_status', filters.status);
      }

      const { data, error } = await query.order('review_date', { ascending: false });

      if (error) throw error;
      return data;
    } catch (error) {
      throw new ServiceError('APP_REVIEWS_FETCH_FAILED', error.message);
    }
  }

  // Review Decision
  async approveApp(reviewId, approvalData, tenant_id) {
    try {
      const tenantUuid = await validateAndGetTenantUuid(tenant_id);
      
      // Update review status
      const { data: review, error: reviewError } = await supabase
        .from(this.reviewsTable)
        .update({
          review_status: 'approved',
          review_notes: approvalData.notes,
          overall_score: approvalData.overallScore,
          next_review_date: approvalData.nextReviewDate
        })
        .eq('id', reviewId)
        .select()
        .single();

      if (reviewError) throw reviewError;

      // Update app status to active
      const { error: appError } = await supabase
        .from(this.appsTable)
        .update({
          app_status: 'active',
          published_at: new Date().toISOString()
        })
        .eq('id', review.app_id)
        .eq('tenant_id', tenantUuid);

      if (appError) throw appError;

      return review;
    } catch (error) {
      throw new ServiceError('APP_APPROVAL_FAILED', error.message);
    }
  }

  async rejectApp(reviewId, rejectionData, tenant_id) {
    try {
      const tenantUuid = await validateAndGetTenantUuid(tenant_id);
      
      // Update review status
      const { data: review, error: reviewError } = await supabase
        .from(this.reviewsTable)
        .update({
          review_status: 'rejected',
          review_notes: rejectionData.notes,
          overall_score: rejectionData.overallScore
        })
        .eq('id', reviewId)
        .select()
        .single();

      if (reviewError) throw reviewError;

      // Update app status to rejected
      const { error: appError } = await supabase
        .from(this.appsTable)
        .update({
          app_status: 'rejected'
        })
        .eq('id', review.app_id)
        .eq('tenant_id', tenantUuid);

      if (appError) throw appError;

      return review;
    } catch (error) {
      throw new ServiceError('APP_REJECTION_FAILED', error.message);
    }
  }

  async requestRevision(reviewId, revisionData, tenant_id) {
    try {
      const tenantUuid = await validateAndGetTenantUuid(tenant_id);
      
      // Update review status
      const { data: review, error: reviewError } = await supabase
        .from(this.reviewsTable)
        .update({
          review_status: 'needs_revision',
          review_notes: revisionData.notes,
          overall_score: revisionData.overallScore,
          next_review_date: revisionData.nextReviewDate
        })
        .eq('id', reviewId)
        .select()
        .single();

      if (reviewError) throw reviewError;

      // Update app status to draft
      const { error: appError } = await supabase
        .from(this.appsTable)
        .update({
          app_status: 'draft'
        })
        .eq('id', review.app_id)
        .eq('tenant_id', tenantUuid);

      if (appError) throw appError;

      return review;
    } catch (error) {
      throw new ServiceError('REVISION_REQUEST_FAILED', error.message);
    }
  }

  // Review Statistics
  async getReviewStatistics(tenant_id, dateRange = {}) {
    try {
      const tenantUuid = await validateAndGetTenantUuid(tenant_id);
      
      const { data: reviews, error } = await supabase
        .from(this.reviewsTable)
        .select(`
          review_status,
          overall_score,
          review_date,
          app:apps (
            app_category,
            tenant_id
          )
        `)
        .eq('app.tenant_id', tenantUuid);

      if (error) throw error;

      // Calculate statistics
      const stats = {
        totalReviews: reviews.length,
        pendingReviews: reviews.filter(r => r.review_status === 'pending').length,
        approvedReviews: reviews.filter(r => r.review_status === 'approved').length,
        rejectedReviews: reviews.filter(r => r.review_status === 'rejected').length,
        revisionRequests: reviews.filter(r => r.review_status === 'needs_revision').length,
        averageScore: reviews.length > 0 ? 
          reviews.reduce((sum, r) => sum + (r.overall_score || 0), 0) / reviews.length : 0,
        reviewsByCategory: {}
      };

      // Group by category
      reviews.forEach(review => {
        const category = review.app?.app_category || 'Unknown';
        if (!stats.reviewsByCategory[category]) {
          stats.reviewsByCategory[category] = 0;
        }
        stats.reviewsByCategory[category]++;
      });

      return stats;
    } catch (error) {
      throw new ServiceError('REVIEW_STATISTICS_FETCH_FAILED', error.message);
    }
  }

  // Assign Reviewers
  async assignReviewer(reviewId, reviewerId, tenant_id) {
    try {
      const tenantUuid = await validateAndGetTenantUuid(tenant_id);
      
      const { data, error } = await supabase
        .from(this.reviewsTable)
        .update({
          reviewer_id: reviewerId,
          review_date: new Date().toISOString()
        })
        .eq('id', reviewId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      throw new ServiceError('REVIEWER_ASSIGNMENT_FAILED', error.message);
    }
  }
}

export const appAdminReviewService = new AppAdminReviewService();
